import { useCallback, useEffect, useState } from 'react';

/** New preference key. Product slug is os-buddy; this is not a legacy vehiclespace key. */
export const WORK_ORDER_CREATE_LAYOUT_KEY = 'os-buddy-work-order-create-layout';

/** Wide enough for the notes panel, the wizard and the history panel side by side. */
export const DEFAULT_DIALOG_WIDTH = 1440;
export const DEFAULT_DIALOG_HEIGHT = 920;
export const MIN_DIALOG_WIDTH = 760;
export const MIN_DIALOG_HEIGHT = 520;
export const MIN_SIDE_PANEL_WIDTH = 220;
export const MAX_SIDE_PANEL_WIDTH = 560;
/** Wizard column that must remain usable beside the side panels. */
export const MIN_MAIN_COLUMN_WIDTH = 420;
/** Matches the dialog container's inset so the paper stays on screen. */
export const VIEWPORT_GUTTER = 64;
export const SIDE_PANEL_KEYBOARD_STEP = 16;
/** Centered paper: the edge moves half of the size change, so a 16px step is 32px of size. */
export const DIALOG_KEYBOARD_STEP = 32;

export interface WorkOrderCreateLayout {
  dialogWidth: number;
  dialogHeight: number;
  /** Notes panel on the left. */
  sidePanelWidth: number;
  /** Service-history summary panel on the right. */
  historyPanelWidth: number;
}

export const DEFAULT_WORK_ORDER_CREATE_LAYOUT: WorkOrderCreateLayout = {
  dialogWidth: DEFAULT_DIALOG_WIDTH,
  dialogHeight: DEFAULT_DIALOG_HEIGHT,
  sidePanelWidth: 280,
  historyPanelWidth: 320,
};

export interface ViewportSize {
  width: number;
  height: number;
}

function finiteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Width range for one side panel, leaving room for the wizard and the other panel. */
export function sidePanelBounds(
  dialogWidth: number,
  otherPanelWidth = 0,
): { min: number; max: number } {
  const room = Math.max(MIN_SIDE_PANEL_WIDTH, dialogWidth - MIN_MAIN_COLUMN_WIDTH - otherPanelWidth);
  return { min: MIN_SIDE_PANEL_WIDTH, max: Math.min(MAX_SIDE_PANEL_WIDTH, room) };
}

/** Limit the saved dialog to the screen. The panels keep their own preferred widths. */
export function clampLayoutToViewport(
  layout: WorkOrderCreateLayout,
  viewport: ViewportSize,
): WorkOrderCreateLayout {
  const maxWidth = Math.max(MIN_DIALOG_WIDTH, viewport.width - VIEWPORT_GUTTER);
  const maxHeight = Math.max(MIN_DIALOG_HEIGHT, viewport.height - VIEWPORT_GUTTER);
  return {
    dialogWidth: clamp(layout.dialogWidth, MIN_DIALOG_WIDTH, maxWidth),
    dialogHeight: clamp(layout.dialogHeight, MIN_DIALOG_HEIGHT, maxHeight),
    sidePanelWidth: clamp(layout.sidePanelWidth, MIN_SIDE_PANEL_WIDTH, MAX_SIDE_PANEL_WIDTH),
    historyPanelWidth: clamp(layout.historyPanelWidth, MIN_SIDE_PANEL_WIDTH, MAX_SIDE_PANEL_WIDTH),
  };
}

/**
 * Panel widths used on screen, so a narrow dialog does not cover the wizard.
 * With the history panel shown, the notes panel is fitted first and the
 * history panel takes what is left.
 */
export function layoutForDisplay(
  stored: WorkOrderCreateLayout,
  viewport: ViewportSize,
  showHistoryPanel = false,
): WorkOrderCreateLayout {
  const fitted = clampLayoutToViewport(stored, viewport);
  const notesBounds = sidePanelBounds(fitted.dialogWidth, showHistoryPanel ? MIN_SIDE_PANEL_WIDTH : 0);
  const sidePanelWidth = clamp(fitted.sidePanelWidth, notesBounds.min, notesBounds.max);
  const historyBounds = sidePanelBounds(fitted.dialogWidth, sidePanelWidth);
  return {
    ...fitted,
    sidePanelWidth,
    historyPanelWidth: clamp(fitted.historyPanelWidth, historyBounds.min, historyBounds.max),
  };
}

export function readWorkOrderCreateLayout(
  storage: Pick<Storage, 'getItem'>,
): WorkOrderCreateLayout {
  try {
    const raw = storage.getItem(WORK_ORDER_CREATE_LAYOUT_KEY);
    if (!raw) return { ...DEFAULT_WORK_ORDER_CREATE_LAYOUT };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return { ...DEFAULT_WORK_ORDER_CREATE_LAYOUT };
    const record = parsed as Record<string, unknown>;
    return {
      dialogWidth: finiteNumber(record.dialogWidth, DEFAULT_DIALOG_WIDTH),
      dialogHeight: finiteNumber(record.dialogHeight, DEFAULT_DIALOG_HEIGHT),
      sidePanelWidth: finiteNumber(record.sidePanelWidth, DEFAULT_WORK_ORDER_CREATE_LAYOUT.sidePanelWidth),
      historyPanelWidth: finiteNumber(
        record.historyPanelWidth,
        DEFAULT_WORK_ORDER_CREATE_LAYOUT.historyPanelWidth,
      ),
    };
  } catch {
    return { ...DEFAULT_WORK_ORDER_CREATE_LAYOUT };
  }
}

export function writeWorkOrderCreateLayout(
  storage: Pick<Storage, 'setItem'>,
  layout: WorkOrderCreateLayout,
): void {
  try {
    storage.setItem(WORK_ORDER_CREATE_LAYOUT_KEY, JSON.stringify(layout));
  } catch {
    // Ignore quota and private-mode failures, matching other portal preferences.
  }
}

export function readViewport(): ViewportSize {
  return { width: window.innerWidth, height: window.innerHeight };
}

export function useWorkOrderCreateLayout(showHistoryPanel = false): {
  layout: WorkOrderCreateLayout;
  storedLayout: WorkOrderCreateLayout;
  updateLayout: (next: WorkOrderCreateLayout) => void;
} {
  const [storedLayout, setStoredLayout] = useState(() => readWorkOrderCreateLayout(localStorage));
  const [viewport, setViewport] = useState(readViewport);

  useEffect(() => {
    const onResize = () => { setViewport(readViewport()); };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); };
  }, []);

  const updateLayout = useCallback((next: WorkOrderCreateLayout) => {
    const saved = clampLayoutToViewport(next, readViewport());
    setStoredLayout(saved);
    writeWorkOrderCreateLayout(localStorage, saved);
  }, []);

  return {
    layout: layoutForDisplay(storedLayout, viewport, showHistoryPanel),
    storedLayout,
    updateLayout,
  };
}

/**
 * Follow the pointer from the position where the drag started.
 * A centered dialog edge moves half as far as its width changes, so callers
 * pass a factor of 2 for dialog edges and 1 for the side panels.
 */
export function trackPointerResize(
  event: { button: number; clientX: number; clientY: number; preventDefault: () => void },
  cursor: string,
  apply: (dx: number, dy: number) => void,
): () => void {
  if (event.button !== 0) return () => undefined;
  event.preventDefault();
  const startX = event.clientX;
  const startY = event.clientY;
  const previousCursor = document.body.style.cursor;
  const previousUserSelect = document.body.style.userSelect;
  document.body.style.cursor = cursor;
  document.body.style.userSelect = 'none';

  const move = (ev: PointerEvent) => {
    apply(ev.clientX - startX, ev.clientY - startY);
  };
  const stop = () => {
    document.body.style.cursor = previousCursor;
    document.body.style.userSelect = previousUserSelect;
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', stop);
    window.removeEventListener('pointercancel', stop);
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', stop);
  window.addEventListener('pointercancel', stop);
  return stop;
}
